import { useRef, useCallback } from "react";

export type PhaseType = "PHASE I" | "PHASE II" | "PHASE III" | "idle";

interface PhaseAudioConfig {
  droneFreq: number;
  droneFreq2: number;
  pulseRate: number;
  gainLevel: number;
  reverbMix: number;
}

const PHASE_CONFIGS: Record<PhaseType, PhaseAudioConfig> = {
  idle: { droneFreq: 55, droneFreq2: 82, pulseRate: 0.3, gainLevel: 0.04, reverbMix: 0.3 },
  "PHASE I": { droneFreq: 55, droneFreq2: 82.4, pulseRate: 0.5, gainLevel: 0.07, reverbMix: 0.5 },
  "PHASE II": { droneFreq: 73.4, droneFreq2: 110, pulseRate: 0.8, gainLevel: 0.09, reverbMix: 0.4 },
  "PHASE III": { droneFreq: 110, droneFreq2: 164.8, pulseRate: 0.25, gainLevel: 0.06, reverbMix: 0.7 },
};

export function usePhaseAudio() {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const droneOsc1Ref = useRef<OscillatorNode | null>(null);
  const droneOsc2Ref = useRef<OscillatorNode | null>(null);
  const droneOsc3Ref = useRef<OscillatorNode | null>(null);
  const lfoRef = useRef<OscillatorNode | null>(null);
  const lfoGainRef = useRef<GainNode | null>(null);
  const currentPhaseRef = useRef<PhaseType>("idle");
  const startedRef = useRef(false);

  // リバーブ用インパルス生成
  const createReverb = useCallback((ctx: AudioContext, duration = 3, decay = 2): ConvolverNode => {
    const convolver = ctx.createConvolver();
    const rate = ctx.sampleRate;
    const length = rate * duration;
    const impulse = ctx.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    convolver.buffer = impulse;
    return convolver;
  }, []);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const ctx = new AudioContext();
    ctxRef.current = ctx;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0, ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(0.07, ctx.currentTime + 3);
    masterGainRef.current = masterGain;

    const reverb = createReverb(ctx, 4, 2.5);
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0.4;

    // ドライ/ウェット
    const dryGain = ctx.createGain();
    dryGain.gain.value = 0.6;

    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(reverbGain);
    dryGain.connect(ctx.destination);
    reverbGain.connect(ctx.destination);

    // LFO（振幅変調）
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.4;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.015;
    lfo.connect(lfoGain);
    lfo.start();
    lfoRef.current = lfo;
    lfoGainRef.current = lfoGain;

    // ドローン1（基音）
    const osc1 = ctx.createOscillator();
    osc1.type = "sawtooth";
    osc1.frequency.value = 55;
    const g1 = ctx.createGain();
    g1.gain.value = 0.4;
    lfoGain.connect(g1.gain);
    osc1.connect(g1);
    g1.connect(masterGain);
    osc1.start();
    droneOsc1Ref.current = osc1;

    // ドローン2（5度上）
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 82.4;
    const g2 = ctx.createGain();
    g2.gain.value = 0.3;
    osc2.connect(g2);
    g2.connect(masterGain);
    osc2.start();
    droneOsc2Ref.current = osc2;

    // ドローン3（倍音）
    const osc3 = ctx.createOscillator();
    osc3.type = "triangle";
    osc3.frequency.value = 110;
    const g3 = ctx.createGain();
    g3.gain.value = 0.15;
    osc3.connect(g3);
    g3.connect(masterGain);
    osc3.start();
    droneOsc3Ref.current = osc3;
  }, [createReverb]);

  const transitionToPhase = useCallback((phase: PhaseType) => {
    if (!ctxRef.current || !startedRef.current) return;
    if (currentPhaseRef.current === phase) return;
    currentPhaseRef.current = phase;

    const ctx = ctxRef.current;
    const cfg = PHASE_CONFIGS[phase];
    const now = ctx.currentTime;
    const ramp = 2.5; // 遷移時間（秒）

    // 周波数を滑らかに変化
    if (droneOsc1Ref.current) {
      droneOsc1Ref.current.frequency.linearRampToValueAtTime(cfg.droneFreq, now + ramp);
    }
    if (droneOsc2Ref.current) {
      droneOsc2Ref.current.frequency.linearRampToValueAtTime(cfg.droneFreq2, now + ramp);
    }
    if (droneOsc3Ref.current) {
      droneOsc3Ref.current.frequency.linearRampToValueAtTime(cfg.droneFreq * 2, now + ramp);
    }

    // マスターゲイン変化
    if (masterGainRef.current) {
      masterGainRef.current.gain.linearRampToValueAtTime(cfg.gainLevel, now + ramp);
    }

    // LFO速度変化（鼓動感）
    if (lfoRef.current) {
      lfoRef.current.frequency.linearRampToValueAtTime(cfg.pulseRate, now + ramp);
    }

    // PHASE III：倍音を強調
    if (phase === "PHASE III" && droneOsc3Ref.current) {
      droneOsc3Ref.current.type = "sine";
    } else if (droneOsc3Ref.current) {
      droneOsc3Ref.current.type = "triangle";
    }
  }, []);

  // フェーズ移行時の一発音（鐘のような）
  const playPhaseChime = useCallback((phase: PhaseType) => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;

    const freqMap: Record<PhaseType, number> = {
      idle: 220,
      "PHASE I": 220,
      "PHASE II": 329.6,
      "PHASE III": 440,
    };

    const freq = freqMap[phase];
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 3.5);

    // 倍音
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = freq * 2.01;
    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0.05, ctx.currentTime);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.5);

    osc.connect(gain);
    osc2.connect(gain2);
    gain.connect(ctx.destination);
    gain2.connect(ctx.destination);

    osc.start();
    osc2.start();
    osc.stop(ctx.currentTime + 3.5);
    osc2.stop(ctx.currentTime + 2.5);
  }, []);

  // 署名時の神聖な和音
  const playSigningChord = useCallback(() => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;
    const freqs = [220, 277.2, 329.6, 440, 554.4];

    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.15);
      gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + i * 0.15 + 0.3);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 5);
    });
  }, []);

  const stop = useCallback((fadeTime = 2) => {
    if (!ctxRef.current || !masterGainRef.current) return;
    const ctx = ctxRef.current;
    masterGainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + fadeTime);
    setTimeout(() => {
      try { ctx.close(); } catch (_) { /* ignore */ }
      startedRef.current = false;
    }, (fadeTime + 0.5) * 1000);
  }, []);

  return { start, transitionToPhase, playPhaseChime, playSigningChord, stop };
}
